'use client'

import { Fragment, useState, useEffect, useMemo } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import ReviewsSection from './ReviewsSection'
import FavoriteButton from './FavoriteButton'
import { Professional } from '../lib/professionalService'
import { getUserReviewSummary, ReviewSummary } from '../lib/reviewService'
import { convertAvailabilityFromGMT, getUserTimezone } from '../utils/timezoneUtils'

interface ProfessionalProfileModalProps {
  professional: Professional | null
  open: boolean
  onClose: () => void
  onBookSession?: (professional: Professional) => void
}

// Get short specialization label
function getSpecLabel(spec: string): string {
  return spec.replace(' Engineer', '').replace(' Developer', '')
}

export default function ProfessionalProfileModal({ professional, open, onClose, onBookSession }: ProfessionalProfileModalProps) {
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null)

  // Load review summary when professional changes
  useEffect(() => {
    const loadReviewSummary = async () => {
      if (professional?.uid) {
        try {
          const summary = await getUserReviewSummary(professional.uid)
          setReviewSummary(summary)
        } catch (error) {
          console.error('Error loading review summary:', error)
          setReviewSummary(null)
        }
      }
    }
    loadReviewSummary()
  }, [professional?.uid])

  // Compute availability for "This week" pills
  const weekAvailability = useMemo(() => {
    if (!professional?.availability) return null
    try {
      const tz = getUserTimezone(professional.timezone || professional.professionalTimezone)
      const local = convertAvailabilityFromGMT(professional.availability as any, tz)
      const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
      const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      return dayKeys.map((key, i) => {
        const dayData = (local as any)[key]
        const isAvailable = dayData?.enabled && dayData?.timeSlots?.length > 0
        return { label: dayLabels[i], available: isAvailable }
      })
    } catch {
      return null
    }
  }, [professional?.availability, professional?.timezone, professional?.professionalTimezone])

  if (!professional) return null

  const hasReviews = reviewSummary && reviewSummary.totalReviews > 0
  const specLabel = getSpecLabel(professional.specialization || professional.title || 'Engineer')
  const tz = professional.timezone || professional.professionalTimezone || ''

  // Format timezone display
  const tzDisplay = (() => {
    if (!tz) return ''
    try {
      const now = new Date()
      const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      const parts = formatter.formatToParts(now)
      const offset = parts.find(p => p.type === 'timeZoneName')?.value || ''
      return `${tz.replace(/_/g, ' ')} · ${offset}`
    } catch {
      return tz
    }
  })()

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-8 sm:px-6 sm:py-12 md:px-6 md:py-16">
          <div className="flex min-h-full items-start justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.985]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-[960px] overflow-hidden rounded-2xl md:rounded-[20px] relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 md:top-[18px] md:right-[18px] w-[34px] h-[34px] rounded-full inline-flex items-center justify-center z-10 cursor-pointer transition-colors"
                  style={{ background: 'rgba(0,0,0,0.45)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16)', backdropFilter: 'blur(6px)' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                <div className="grid grid-cols-1 md:grid-cols-[380px_1fr]">
                  {/* LEFT: photo + identity + actions */}
                  <div className="relative" style={{ borderRight: '1px solid hsl(0 0% 20% / 0.4)' }}>
                    {/* Photo — shorter on mobile, taller portrait on desktop */}
                    <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, hsl(0 0% 22%), hsl(0 0% 6%))' }}>
                      <div className="aspect-[3/2] md:aspect-[4/5]"></div>
                      <div className="absolute inset-0">
                        <div className="dot-grid" style={{ position: 'absolute', inset: 0, opacity: 0.3 }}></div>

                        {professional.profileImage ? (
                          <img
                            src={professional.profileImage}
                            alt={professional.displayName}
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{ zIndex: 1 }}
                          />
                        ) : (
                          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 250" fill="none" stroke="currentColor" strokeWidth="1.4" style={{ color: 'hsl(0 0% 65% / 0.45)' }}>
                            <circle cx="100" cy="95" r="34"/>
                            <path d="M30 240c6-38 38-58 70-58s64 20 70 58"/>
                          </svg>
                        )}

                        {/* Gradient overlay */}
                        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.7) 100%)', zIndex: 2 }}></div>

                        {/* Rate badge */}
                        <span className="badge-rate" style={{ zIndex: 3 }}>
                          ${professional.sessionRate || 0} <span className="opacity-70 font-medium ml-0.5">/ session</span>
                        </span>

                        {/* Name overlay */}
                        <div className="absolute left-5 right-5 bottom-5 md:left-6 md:right-6 md:bottom-6" style={{ zIndex: 3 }}>
                          <div className="text-xl md:text-[22px] font-medium tracking-tight-half leading-tight" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.6)' }}>
                            {professional.displayName}
                          </div>
                          <div className="text-[12.5px] mt-1.5" style={{ color: 'hsl(0 0% 88%)', textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
                            {specLabel}{professional.company && <> · <span className="opacity-90">{professional.company}</span></>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="p-5 md:p-6 flex flex-col gap-3" style={{ borderTop: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <button
                        onClick={() => onBookSession?.(professional)}
                        className="btn btn-primary justify-center w-full !py-3.5"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
                        Book a 30-min session
                      </button>
                      <FavoriteButton
                        professionalId={professional.uid}
                        size="md"
                        showLabel
                        className="!w-full !rounded-[10px] !bg-transparent !shadow-[inset_0_0_0_1px_hsl(0_0%_20%)] hover:!bg-white/[0.04] !py-2.5 !justify-center"
                      />

                      {/* Experience + Rating stat cards */}
                      <div className="grid grid-cols-2 gap-2.5 mt-3">
                        <div className="rounded-xl p-4 text-center" style={{ boxShadow: 'inset 0 0 0 1px hsl(0 0% 20% / 0.5)' }}>
                          <div className="text-[10px] tracking-wide-3 uppercase text-white/50">Experience</div>
                          <div className="text-[16px] font-medium mt-2">{professional.experience || '—'}</div>
                        </div>
                        <div className="rounded-xl p-4 text-center" style={{ boxShadow: 'inset 0 0 0 1px hsl(0 0% 20% / 0.5)' }}>
                          <div className="text-[10px] tracking-wide-3 uppercase text-white/50">Rating</div>
                          <div className="text-[16px] font-medium mt-2">
                            {hasReviews ? (
                              <span className="inline-flex items-center gap-1.5">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="text-white/85"><path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/></svg>
                                {reviewSummary!.averageRating.toFixed(1)}
                              </span>
                            ) : (
                              <span className="italic text-white/50">New</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: scrollable content */}
                  <div className="p-6 md:p-9 flex flex-col gap-8 md:gap-9 md:overflow-y-auto md:max-h-[80vh]">

                    {/* About */}
                    {professional.bio && (
                      <section>
                        <h3 className="text-[15px] font-medium tracking-tight-half mb-4">About</h3>
                        <p className="text-white/85 text-[13.5px] leading-[1.7] whitespace-pre-line">
                          {professional.bio}
                        </p>
                      </section>
                    )}

                    {/* This week — availability */}
                    {weekAvailability && (
                      <section>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-[15px] font-medium tracking-tight-half">This week</h3>
                          {tzDisplay && <span className="text-[11px] text-white/50 hidden sm:block">{tzDisplay}</span>}
                        </div>
                        <div className="grid grid-cols-7 gap-1.5 sm:gap-2.5">
                          {weekAvailability.map((day, i) => (
                            <div
                              key={i}
                              className="text-center py-3 sm:py-3.5 rounded-[10px]"
                              style={{
                                boxShadow: day.available
                                  ? 'inset 0 0 0 1px hsl(0 0% 100% / 0.35)'
                                  : 'inset 0 0 0 1px hsl(0 0% 20% / 0.55)',
                                background: day.available ? 'rgba(255,255,255,0.04)' : 'transparent',
                              }}
                            >
                              <div className="text-[9px] sm:text-[10px] uppercase" style={{ letterSpacing: '2px', color: day.available ? 'hsl(0 0% 65% / 0.7)' : 'hsl(0 0% 65% / 0.45)' }}>
                                {day.label}
                              </div>
                              {/* Narrow/two-col: dot indicator; Wide: text label */}
                              <div className="mt-1.5 flex items-center justify-center">
                                <span className="hidden lg:inline text-[11.5px] font-medium" style={{ color: day.available ? 'hsl(0 0% 100%)' : 'hsl(0 0% 65% / 0.55)' }}>
                                  {day.available ? 'Available' : 'Off'}
                                </span>
                                <span
                                  className="lg:hidden inline-block w-[6px] h-[6px] rounded-full"
                                  style={{ background: day.available ? 'hsl(0 0% 100%)' : 'hsl(0 0% 30%)' }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        {tzDisplay && <div className="text-white/40 text-[11px] mt-3 sm:hidden">{tzDisplay}</div>}
                        <div className="text-white/50 text-[12px] mt-4 flex items-center gap-2">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                          Sessions are 30 minutes. We auto-adjust times to your local timezone.
                        </div>
                      </section>
                    )}

                    {/* Reviews */}
                    <section>
                      <ReviewsSection userId={professional.uid} />
                    </section>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
